import { useState, useEffect, useRef } from 'react';
import { Camera, MapPin, Shield, Fingerprint, Gift, CheckCircle } from 'lucide-react';
import MapPickerModal from '../MapPickerModal';
import apiService from '../../apiService';
import '../../styles/Auth.css';

// ── OTP boxes (Auth.css .otp-container / .otp-input) ─────────────────────────
const OtpBoxes = ({ value, onChange }) => {
  const digits = (value + '      ').slice(0, 6).split('');
  const r0 = useRef(null), r1 = useRef(null), r2 = useRef(null);
  const r3 = useRef(null), r4 = useRef(null), r5 = useRef(null);
  const refs = [r0, r1, r2, r3, r4, r5];

  const handle = (i, e) => {
    const ch = e.target.value.replace(/\D/g, '').slice(-1);
    const next = value.slice(0, i) + (ch || ' ') + value.slice(i + 1);
    onChange(next.trimEnd());
    if (ch && i < 5) refs[i + 1].current?.focus();
  };
  const handleKey = (i, e) => {
    if (e.key === 'Backspace' && !digits[i].trim() && i > 0) {
      refs[i - 1].current?.focus();
      onChange((value.slice(0, i - 1) + ' ' + value.slice(i)).trimEnd());
    }
  };
  return (
    <div className="otp-container">
      {digits.map((d, i) => (
        <input
          key={i} ref={refs[i]}
          type="tel" inputMode="numeric" maxLength={1}
          value={d.trim()}
          onChange={e => handle(i, e)}
          onKeyDown={e => handleKey(i, e)}
          className={`otp-input${d.trim() ? ' filled' : ''}`}
        />
      ))}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const ProfileCompletionModal = ({
  userProfile, setUserProfile,
  handleProfilePicUpload,
  setShowTwoFactorSetup, twoFactorEnabled,
  biometricAvailable, biometricEnabled, enableBiometric,
  handleProfileSubmit,
  verifiedMobile,
}) => {
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [errors, setErrors]               = useState({});
  const [step, setStep]                   = useState('form');
  const [emailOtp, setEmailOtp]           = useState('');
  const [sending, setSending]             = useState(false);
  const [verifying, setVerifying]         = useState(false);
  const [otpError, setOtpError]           = useState('');
  // Pre-mark verified if the backend already recorded email_verified_at for this email
  const [emailVerified, setEmailVerified] = useState(Boolean(userProfile?.email_verified_at));
  const [verifiedEmail, setVerifiedEmail] = useState(userProfile?.email_verified_at ? (userProfile.email || '') : '');
  const [resendTimer, setResendTimer]     = useState(0);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  useEffect(() => {
    const patch = {};
    if (!userProfile.city)    patch.city    = 'Gurgaon';
    if (!userProfile.state)   patch.state   = 'Haryana';
    if (!userProfile.country) patch.country = 'India';
    if (Object.keys(patch).length) setUserProfile(p => ({ ...p, ...patch }));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (verifiedMobile && !userProfile.mobile)
      setUserProfile(p => ({ ...p, mobile: verifiedMobile }));
  }, [verifiedMobile]); // eslint-disable-line

  const validate = () => {
    const e = {};
    if (!userProfile.name?.trim())    e.name    = 'Full name is required';
    if (!userProfile.email?.trim())   e.email   = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userProfile.email)) e.email = 'Enter a valid email address';
    if (!userProfile.address?.trim()) e.address = 'Delivery address is required';
    if (!userProfile.city?.trim())    e.city    = 'City is required';
    if (!userProfile.state?.trim())   e.state   = 'State is required';
    if (!userProfile.pincode || String(userProfile.pincode).length !== 6)
      e.pincode = 'Enter a valid 6-digit pincode';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleNext = async () => {
    if (!validate()) return;
    if (emailVerified) { handleProfileSubmit(); return; }
    setSending(true); setOtpError('');
    try {
      const res = await apiService.request('/profile/verify-email/send', {
        method: 'POST',
        body: JSON.stringify({ email: userProfile.email }),
        auth: false,
      });
      if (res.success || res.status === 200) {
        setStep('verify-email');
        setResendTimer(30);
      } else {
        setOtpError(res.data?.message || 'Could not send OTP. Please try again.');
      }
    } catch {
      setOtpError('Network error. Please check your connection.');
    } finally { setSending(false); }
  };

  const handleVerify = async () => {
    const code = emailOtp.replace(/\s/g, '');
    if (code.length < 6) { setOtpError('Enter the full 6-digit code.'); return; }
    setVerifying(true); setOtpError('');
    try {
      const res = await apiService.request('/profile/verify-email/confirm', {
        method: 'POST',
        body: JSON.stringify({ email: userProfile.email, otp: code }),
        auth: false,
      });
      if (res.success || res.status === 200) {
        setEmailVerified(true);
        setVerifiedEmail(userProfile.email || '');
        handleProfileSubmit();
      } else {
        setOtpError(res.data?.message || 'Incorrect OTP. Please try again.');
      }
    } catch { setOtpError('Verification failed. Please try again.'); }
    finally { setVerifying(false); }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setEmailOtp(''); setOtpError(''); setSending(true);
    try {
      await apiService.request('/profile/verify-email/send', {
        method: 'POST', body: JSON.stringify({ email: userProfile.email }), auth: false,
      });
      setResendTimer(30);
    } finally { setSending(false); }
  };

  const handleMapConfirm = (data) => {
    setUserProfile(p => ({
      ...p,
      address:   data.address   || p.address,
      city:      data.city      || p.city,
      state:     data.state     || p.state,
      pincode:   data.pincode   || p.pincode,
      country:   data.country   || p.country,
      latitude:  data.latitude  ?? p.latitude,
      longitude: data.longitude ?? p.longitude,
    }));
    setShowMapPicker(false);
  };

  const isMobileVerified = verifiedMobile &&
    (userProfile.mobile || '').replace(/\D/g, '').endsWith(
      verifiedMobile.replace(/\D/g, '').slice(-10)
    );

  // ── Email OTP verification screen ─────────────────────────────────────────
  if (step === 'verify-email') {
    return (
      <div className="modal-overlay" style={{ zIndex: 220 }}>
        <div className="signup-container">
          <div className="signup-card">
            <div className="signup-header">
              <div className="logo" style={{ background: 'linear-gradient(135deg,#7c3aed,#9333ea)', fontSize: 28 }}>✉️</div>
              <h2>Verify Your Email</h2>
              <p className="subtitle">
                We sent a 6-digit code to<br />
                <strong style={{ color: '#4f46e5' }}>{userProfile.email}</strong>
              </p>
            </div>

            <OtpBoxes value={emailOtp} onChange={setEmailOtp} />

            {otpError && (
              <div className="field-error" style={{ justifyContent: 'center', marginTop: 8 }}>
                <span>⚠️</span><span>{otpError}</span>
              </div>
            )}

            <div className="otp-timer">
              {resendTimer > 0
                ? `Resend code in ${resendTimer}s`
                : <button className="resend-btn" onClick={handleResend} disabled={sending}>
                    {sending ? 'Sending…' : 'Resend OTP'}
                  </button>
              }
            </div>

            <button
              className="submit-btn"
              onClick={handleVerify}
              disabled={verifying}
              style={{ marginTop: 24 }}
            >
              {verifying ? <><span className="loading-spinner" /> Verifying…</> : '✓ Verify & Complete Profile'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button
                className="resend-btn"
                onClick={() => { setStep('form'); setEmailOtp(''); setOtpError(''); }}
              >
                ← Change email address
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Profile form ──────────────────────────────────────────────────────────
  return (
    <>
    <div className="modal-overlay" style={{ zIndex: 220, alignItems: 'flex-start', padding: '20px 0' }}>
      <div className="signup-container">
        <div className="signup-card">

          {/* Header — avatar */}
          <div className="signup-header" style={{ marginBottom: 24 }}>
            <div style={{ position: 'relative', display: 'inline-block', marginBottom: 12 }}>
              <div style={{
                width: 72, height: 72, borderRadius: '50%',
                background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
              }}>
                {userProfile.profilePic
                  ? <img src={userProfile.profilePic} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: 32 }}>👤</span>
                }
              </div>
              <label style={{
                position: 'absolute', bottom: 0, right: 0,
                background: '#7c3aed', color: '#fff', borderRadius: '50%',
                width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
              }}>
                <Camera size={13} />
                <input type="file" accept="image/*" onChange={handleProfilePicUpload} style={{ display: 'none' }} />
              </label>
            </div>
            <h2>Complete Your Profile</h2>
            <p className="subtitle">Help us serve you better</p>
          </div>

          {/* Full Name */}
          <div className="form-group">
            <label>Full Name <span style={{ color: '#ef4444' }}>*</span></label>
            <div className="input-wrapper">
              <input
                type="text"
                value={userProfile.name}
                onChange={e => { setUserProfile(p => ({ ...p, name: e.target.value })); setErrors(p => ({ ...p, name: '' })); }}
                placeholder="Your full name"
                className={errors.name ? 'error' : (userProfile.name ? 'success' : '')}
              />
            </div>
            {errors.name && <div className="field-error"><span>⚠️</span><span>{errors.name}</span></div>}
          </div>

          {/* Email */}
          <div className="form-group">
            <label>
              Email Address <span style={{ color: '#ef4444' }}>*</span>
              {emailVerified && (
                <span style={{ marginLeft: 8, color: '#10b981', fontSize: 12, fontWeight: 600 }}>
                  <CheckCircle size={12} style={{ display: 'inline', marginRight: 2 }} /> Verified
                </span>
              )}
            </label>
            <div className="input-wrapper">
              <input
                type="email"
                value={userProfile.email}
                onChange={e => {
                  const val = e.target.value;
                  setUserProfile(p => ({ ...p, email: val }));
                  setErrors(p => ({ ...p, email: '' }));
                  // Keep "verified" only while the email matches the verified one
                  setEmailVerified(val.trim().toLowerCase() === verifiedEmail.trim().toLowerCase() && !!verifiedEmail);
                }}
                placeholder="your@email.com"
                className={errors.email ? 'error' : (userProfile.email && !errors.email ? 'success' : '')}
              />
            </div>
            {errors.email && <div className="field-error"><span>⚠️</span><span>{errors.email}</span></div>}
          </div>

          {/* Mobile */}
          <div className="form-group">
            <label>
              Mobile Number
              {isMobileVerified && (
                <span style={{ marginLeft: 8, color: '#10b981', fontSize: 12, fontWeight: 600 }}>
                  <CheckCircle size={12} style={{ display: 'inline', marginRight: 2 }} /> Verified
                </span>
              )}
            </label>
            <div className="input-wrapper">
              <input
                type="tel"
                value={userProfile.mobile || ''}
                onChange={e => setUserProfile(p => ({ ...p, mobile: e.target.value }))}
                placeholder="10-digit mobile number"
                className={isMobileVerified ? 'success' : ''}
              />
            </div>
          </div>

          {/* Delivery Address */}
          <div className="form-group">
            <label>Delivery Address <span style={{ color: '#ef4444' }}>*</span></label>
            <div className="input-wrapper">
              <textarea
                value={userProfile.address}
                onChange={e => { setUserProfile(p => ({ ...p, address: e.target.value })); setErrors(p => ({ ...p, address: '' })); }}
                placeholder="House/flat no., street, area"
                rows={2}
                style={{
                  width: '100%', padding: '12px 16px', border: `2px solid ${errors.address ? '#ef4444' : '#e5e7eb'}`,
                  borderRadius: 8, fontSize: 15, outline: 'none', resize: 'vertical',
                  fontFamily: 'inherit', boxSizing: 'border-box', transition: 'border-color 0.2s',
                }}
              />
            </div>
            {errors.address && <div className="field-error"><span>⚠️</span><span>{errors.address}</span></div>}
            <button
              type="button"
              onClick={() => setShowMapPicker(true)}
              style={{ marginTop: 6, background: 'none', border: 'none', color: '#7c3aed', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: 0 }}
            >
              <MapPin size={14} /> Pick from map
            </button>
          </div>

          {/* City */}
          <div className="form-group">
            <label>City <span style={{ color: '#ef4444' }}>*</span></label>
            <div className="input-wrapper">
              <input
                type="text"
                value={userProfile.city}
                onChange={e => { setUserProfile(p => ({ ...p, city: e.target.value })); setErrors(p => ({ ...p, city: '' })); }}
                placeholder="City"
                className={errors.city ? 'error' : (userProfile.city ? 'success' : '')}
              />
            </div>
            {errors.city && <div className="field-error"><span>⚠️</span><span>{errors.city}</span></div>}
          </div>

          {/* Pincode + State */}
          <div className="form-group">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label>Pincode <span style={{ color: '#ef4444' }}>*</span></label>
                <div className="input-wrapper">
                  <input
                    type="text" maxLength={6}
                    value={userProfile.pincode}
                    onChange={e => { setUserProfile(p => ({ ...p, pincode: e.target.value.replace(/\D/g, '') })); setErrors(p => ({ ...p, pincode: '' })); }}
                    placeholder="6-digit"
                    className={errors.pincode ? 'error' : (String(userProfile.pincode).length === 6 ? 'success' : '')}
                  />
                </div>
                {errors.pincode && <div className="field-error"><span>⚠️</span><span>{errors.pincode}</span></div>}
              </div>
              <div>
                <label>State <span style={{ color: '#ef4444' }}>*</span></label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    value={userProfile.state || ''}
                    onChange={e => { setUserProfile(p => ({ ...p, state: e.target.value })); setErrors(p => ({ ...p, state: '' })); }}
                    placeholder="State"
                    className={errors.state ? 'error' : (userProfile.state ? 'success' : '')}
                  />
                </div>
                {errors.state && <div className="field-error"><span>⚠️</span><span>{errors.state}</span></div>}
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="form-group">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ background: '#faf5ff', border: '1.5px solid #e9d5ff', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Shield size={20} color="#7c3aed" />
                  <div>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#1f2937' }}>Two-Factor Auth</p>
                    <p style={{ margin: 0, fontSize: 11, color: '#6b7280' }}>Extra login security</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowTwoFactorSetup(true)}
                  style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: twoFactorEnabled ? '#10b981' : '#7c3aed', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                >
                  {twoFactorEnabled ? 'On ✓' : 'Enable'}
                </button>
              </div>

              {biometricAvailable && (
                <div style={{ background: '#faf5ff', border: '1.5px solid #e9d5ff', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Fingerprint size={20} color="#7c3aed" />
                    <div>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#1f2937' }}>Biometric Login</p>
                      <p style={{ margin: 0, fontSize: 11, color: '#6b7280' }}>Fingerprint / Face ID</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={enableBiometric}
                    style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: biometricEnabled ? '#10b981' : '#7c3aed', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                  >
                    {biometricEnabled ? 'On ✓' : 'Enable'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Welcome offer */}
          <div className="form-group">
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 14px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 20 }}>🎁</span>
              <div>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: '#1e40af' }}>Welcome Offer!</p>
                <p style={{ margin: 0, fontSize: 12, color: '#1d4ed8', marginTop: 2 }}>
                  Complete your profile and get ₹50 off on your first order above ₹200
                </p>
              </div>
            </div>
          </div>

          {otpError && (
            <div className="error-message-general" style={{ marginBottom: 16 }}>
              <span>⚠️</span><span>{otpError}</span>
            </div>
          )}

          <button className="submit-btn" onClick={handleNext} disabled={sending}>
            {sending ? <><span className="loading-spinner" /> Sending verification…</> : 'Complete Profile & Verify Email →'}
          </button>

          <p style={{ textAlign: 'center', fontSize: 12, color: '#9ca3af', marginTop: 12 }}>
            A verification code will be sent to your email
          </p>
        </div>
      </div>
    </div>

    {showMapPicker && (
      <MapPickerModal
        onClose={() => setShowMapPicker(false)}
        onConfirm={handleMapConfirm}
        initialLat={userProfile.latitude || null}
        initialLng={userProfile.longitude || null}
        style={{ zIndex: 230 }}
      />
    )}
    </>
  );
};

export default ProfileCompletionModal;
